import React, { useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  bulkUpdateRequestedItemMutation,
  createEOneItem,
  updateRequestedItemMutation,
  checkForEOneByBarcode,
  createAuditMutation,
  markRequestedItemComplete,
  checkForExistingTagNumber,
} from './api';
import Statuses from './components/Statuses';
import * as selectDropdowns from './utils';
import { Comments } from '../Comments';
import { TooltipButton } from '../TooltipButton';

interface Props {
  data: any;
  queryData: any;
}

const EditRequestedItemForm = (props: Props) => {
  const [updateRequestedItem] = useMutation(updateRequestedItemMutation);
  // const [createEOneFromReqItem] = useMutation(createEOneItem);
  const [bulkUpdate] = useMutation(bulkUpdateRequestedItemMutation);
  const [createAudit] = useMutation(createAuditMutation);
  const [markRIComplete] = useMutation(markRequestedItemComplete);
  const [comment, setComment] = React.useState('');
  const [disabledFieldsVisible, setDisabledFieldsVisible] = React.useState(false);
  const [showDisabledFields, setShowDisabledFields] = React.useState(false);
  const [checkTagNumber] = useLazyQuery(checkForExistingTagNumber, { variables: { barcode: props.queryData.requestedItemById.barcode } });
  let showMoveToEOneButton = true;
  const { user } = useSelector((state: any) => ({
    user: state.user,
  }));

  React.useEffect(() => {
    let enableDisabledFields = false;
    if (user.roles) {
      user.roles.forEach((role) => {
        switch (role.roleId) {
          case 1: // admin
          case 3: // pc - non cap
          case 10: // pc - cap
          case 20: // rec super
          case 21: // rec super - cap
            enableDisabledFields = true;
        }
      });
    }
    setDisabledFieldsVisible(enableDisabledFields);
  }, []);

  const initialFormikVals = {
    id: parseInt(props.queryData.requestedItemById.id),
    barcode: props.queryData.requestedItemById.barcode,
    assetNumber: props.queryData.requestedItemById.assetNumber,
    department: props.queryData.requestedItemById.department,
    deliveryDate: props.queryData.requestedItemById.deliveryDate,
    dateEnteredInEnterpriseOne: props.queryData.requestedItemById.dateEnteredInEnterpriseOne,
    description1: props.queryData.requestedItemById.description1,
    description2: props.queryData.requestedItemById.description2,
    description3: props.queryData.requestedItemById.description3,
    requestItemFYNumber: props.queryData.requestedItemById.requestItemFYNumber,
    serialNumber: props.queryData.requestedItemById.serialNumber,
    purchaseOrderNumber: props.queryData.requestedItemById.purchaseOrderNumber,
    accountNumber: props.queryData.requestedItemById.accountNumber,
    parentNumber: props.queryData.requestedItemById.parentNumber,
    amountPaid: props.queryData.requestedItemById.amountPaid,
    originalQuantity: props.queryData.requestedItemById.originalQuantity,
    currentQuantity: props.queryData.requestedItemById.currentQuantity,
    remarks1: props.queryData.requestedItemById.remarks1,
    remarks2: props.queryData.requestedItemById.remarks2,
    remarks3: props.queryData.requestedItemById.remarks3,
    status: props.queryData.requestedItemById.status,
    physicalOwner: props.queryData.requestedItemById.physicalOwner,
    originalPurchaser: props.queryData.requestedItemById.originalPurchaser,
    newOrUsed: props.queryData.requestedItemById.newOrUsed,
    companyNumber: props.queryData.requestedItemById.companyNumber,
    responsibleBusinessUnit: props.queryData.requestedItemById.responsibleBusinessUnit,
    location: props.queryData.requestedItemById.location,
    majorAccountingClass: props.queryData.requestedItemById.majorAccountingClass,
    majorEquipmentClass: props.queryData.requestedItemById.majorEquipmentClass,
    manufacturer: props.queryData.requestedItemById.manufacturer,
    function: props.queryData.requestedItemById.function,
    categoryCode6: props.queryData.requestedItemById.categoryCode6,
    attachment: props.queryData.requestedItemById.attachment,
    isCovid: props.queryData.requestedItemById.isCovid,
    //below items are used to maintain record in reqItem table, not shown
    req: props.queryData.requestedItemById.request,
    needsImaging: props.queryData.requestedItemById.needsImaging,
  };

  // const reqItemToEone = async (values: any) => {
  //   updateRequestedItem({ variables: values })
  //     .then((res) => {
  //       createEOneFromReqItem({ variables: values })
  //         .then((res) => {
  //           window.location.reload();
  //         })
  //         .catch((err) => {
  //           console.log('Error: /frontend/src/components/RequestForms/EditRequestedItemForm//reqItemToEone', err);
  //         });
  //     })
  //     .catch((err) => {
  //       alert(err);
  //     });

  //   //refresh source grid
  //   // localStorage.setItem('refresh', 'true');
  // };

  const bulkEditReqItems = (values: any) => {
    bulkUpdate({ variables: values })
      .then((res) => {
        window.location.reload();
      })
      .catch((err) => {
        console.log('Error: /frontend/src/components/RequestForms/EditRequestedItemForm//bulkEditReqItems', err);
      });
    //refresh source grid
    // localStorage.setItem('refresh', 'true');
  };

  // check if barcode exist in oracle for new items
  // const { data } = useQuery(checkForExistingTagNumber, {
  //   variables: {
  //     barcode: initialFormikVals.barcode
  //   },
  // });

  // if (data) {
  //   console.log('data: ', data.getOracleItemByTagNumber?.id)
  //   // showMoveToEOneButton = data.findEOneByBarcode == null ? true : false;
  // }

  const createAuditForAssetItem = (formValues) => {
    let commentStr =
      comment !== '' ? `${comment} :: Item ${formValues.requestItemFYNumber}` : `Item ${formValues.requestItemFYNumber} has been updated`;
    let requestId = Number(formValues.requestItemFYNumber.split('-')[1]);
    const audit = {
      barcode: formValues.barcode,
      createdBy: user.adId,
      lastUpdatedBy: user.adId,
      comment: commentStr,
      updatedOn: new Date(),
      entryType: formValues.status,
      requestId: requestId,
    };
    createAudit({ variables: audit }).catch((err) =>
      console.log('Error: /frontend/src/components/RequestForms/EditRequestedItemForm//createAuditForAssetItem', err)
    );
  };

  const formik = useFormik({
    initialValues: initialFormikVals,
    onSubmit: async (values: any, e: any) => {
      if (values.accountNumber) {
        if (!/^[0-9]{4}.[0-9]{4}(\.[0-9]{4})?$/i.test(values.accountNumber)) {
          alert('Invalid account number!');
          return;
        }
      }
      if(values.majorEquipmentClass === 'Oth'){
        alert('Please select a Major Equipment Class.')
        return;
      }
      if (!values.amountPaid || values.amountPaid == 0) {
        alert('Amount Paid must be greater than 0.');
        return;
      }
      const checkTagResponse = await checkTagNumber({ variables: { barcode: values.barcode } });
      if (checkTagResponse) {
        try{
          if (checkTagResponse.data.getOracleItemByTagNumber?.id !== -1) {
          alert('Barcode not unique. Please double check.');
          return;
          }
        }catch(err){
          alert('Barcode not present in Oracle')
          return;
        }        
      }

      values.deliveryDate = values.deliveryDate ? new Date(values.deliveryDate) : null;
      values.amountPaid = parseFloat(values.amountPaid);
      values.isCovid === 'true' ? (values.isCovid = true) : (values.isCovid = false);
      values.originalQuantity = parseFloat(values.originalQuantity);
      values.currentQuantity = parseFloat(values.currentQuantity);
      updateRequestedItem({ variables: values })
        .then((res) => {
          createAuditForAssetItem(values);
          window.location.reload();
        })
        .catch((err) => {
          console.log('Error: /frontend/src/components/RequestForms/EditRequestedItemForm//formik', err);
        });
      //refresh source grid
      localStorage.setItem('refresh', 'true');
    },
  });

  const handleUpdate = async () => {
    formik.submitForm();
  };

  const markComplete = (values: any) => {
    markRIComplete({
      variables: {
        barcode: values.barcode,
      },
    })
      .then((res) => {
        createAuditForAssetItem(values);
      })
      .then(() => window.location.reload())
      .catch((err) => {
        console.log('Error: /frontend/src/components/RequestForms/EditRequestedItemForm//markComplete', err);
      });
  };
  return (
    <Form onSubmit={formik.handleSubmit}>
      <div className='row justify-content-end'>
        <div className='col-sm-9'>
          <Button type='submit' variant='info' className='mt-3 float-left'>
            Save Changes
          </Button>

          <TooltipButton
            variant='outline-info'
            buttonClass='mt-3 ml-3 float-left'
            onClick={() => bulkEditReqItems(formik.values)}
            buttonLabel='Bulk Update'
            tooltipLabel='Update all items from parent request'
          />
          {disabledFieldsVisible && (
            <>
              <TooltipButton
                variant='outline-primary'
                buttonClass='mt-3 ml-3 float-left'
                buttonLabel='Update'
                tooltipLabel='Update without required fields'
                onClick={handleUpdate}
              />
              <TooltipButton
                variant='outline-primary'
                buttonClass='mt-3 ml-3 float-left'
                onClick={() => setShowDisabledFields(!showDisabledFields)}
                buttonLabel='Enable Fields?'
                tooltipLabel='Enable disabled fields'
              />
            </>
          )}

          <TooltipButton
            buttonClass={'mt-3 ml-3 float-left'}
            buttonLabel={'Back'}
            onClick={() => window.history.back()}
            placement={'bottom'}
            tooltipLabel={'Go back'}
          />

          {/* Updated status from dropdown &&
           initial status value from query (hide when already Ready for EOne) && 
           query to check EOne for item */}
          {formik.values.status === 'Ready for EOne' && initialFormikVals.status !== 'Ready for EOne' && (
            <Button variant='danger' className='mt-3 ml-3 float-left' type='submit'>
              Move To EOne Ready
            </Button>
          )}
          {formik.values.status === 'Ready for EOne' && initialFormikVals.status === 'Ready for EOne' && !showMoveToEOneButton && (
            <Button variant='danger' className='mt-3 ml-3 float-left' onClick={() => markComplete(formik.values)}>
              Mark Complete
            </Button>
          )}
        </div>
        <div className='col-sm-3'>
          <Statuses
            formik={formik}
            idName={'status'}
            value={formik.values.status}
            lblClass={''}
            selClass={'form-control col-sm mb-2'}
            label={'Item Status'}
          />
        </div>
      </div>
      <div className='requestDivider'>Asset Information</div>
      <div className='row'>
        <div className='col-sm-8'>
          <div className='row justify-content-start mt-1'>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Asset Number</label>
                <input
                  id='assetNumber'
                  name='assetNumber'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.assetNumber}
                  disabled={!showDisabledFields}
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Barcode (Unit #)</label>
                <input
                  id='barcode'
                  name='barcode'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.barcode}
                  disabled={!showDisabledFields}
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Serial Number</label>
                <input
                  id='serialNumber'
                  name='serialNumber'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.serialNumber}
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Parent Number</label>
                <input
                  id='parentNumber'
                  name='parentNumber'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.parentNumber}
                />
              </div>
            </div>
          </div>
          <div className='row justify-content-start'>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>
                  Company Number&nbsp;<span style={{ color: 'red' }}>*</span>&nbsp; {<i className='fa fa-clone' />}
                </label>
                <div className='form-inline'>
                  <select
                    id='companyNumber'
                    name='companyNumber'
                    className='form-control col-sm'
                    onChange={formik.handleChange}
                    value={formik.values.companyNumber}
                    required={!showDisabledFields}>
                    {selectDropdowns.companyNumbers.map((value, index) => {
                      return (
                        <option value={value.Co}>
                          {value.Co} - {value.Name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>
                  Responsible BU&nbsp;<span style={{ color: 'red' }}>*</span>&nbsp;{<i className='fa fa-clone' />}
                </label>
                <div className='form-inline'>
                  <input
                    id='responsibleBusinessUnit'
                    name='responsibleBusinessUnit'
                    className='form-control col-sm'
                    onChange={formik.handleChange}
                    value={formik.values.responsibleBusinessUnit}
                    required={!showDisabledFields}
                  />
                </div>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Account Number {<i className='fa fa-clone' />}</label>
                <div className='form-inline'>
                  <input
                    id='accountNumber'
                    name='accountNumber'
                    className='form-control col-sm'
                    onChange={formik.handleChange}
                    value={formik.values.accountNumber}
                  />
                </div>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Purchase Order # (AFE)</label>
                <input
                  id='purchaseOrderNumber'
                  name='purchaseOrderNumber'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.purchaseOrderNumber}
                />
              </div>
            </div>
          </div>
          <div className='row justify-content-start'>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>
                  Description 1&nbsp;<span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  id='description1'
                  name='description1'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.description1}
                  required={!showDisabledFields}
                  maxLength={30}
                  disabled={!showDisabledFields}
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Description 2</label>
                <input
                  id='description2'
                  name='description2'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.description2}
                  maxLength={30}
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Description 3</label>
                <input
                  id='description3'
                  name='description3'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.description3}
                  maxLength={30}
                />
              </div>
            </div>
            {formik.values.req && (
              <div className='col-sm-3'>
                <div className='form-group'>
                  <label>Request Info</label>
                  <Button className='form-control col-sm' href={`/receiving/view/requestId=${formik.values.req.requestId}`} variant='info'>
                    View Request {formik.values.req.requestNumber}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className='requestDivider'>Location</div>
          <div className='row justify-content-start mt-1'>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>
                  Location&nbsp;<span style={{color: 'red'}}>*</span>
                </label>
                <input
                  required={!showDisabledFields}
                  id='location'
                  name='location'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.location}
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Original Quantity</label>
                <input
                  id='originalQuantity'
                  name='originalQuantity'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.originalQuantity}
                />
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Current Quantity</label>
                <input
                  id='currentQuantity'
                  name='currentQuantity'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.currentQuantity}
                />
              </div>
            </div>
          </div>
          <div className='requestDivider'>Category Codes 1-10</div>
          <div className='row justify-content-start mt-1'>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Major Accounting Class {<i className='fa fa-clone' />}</label>
                <div className='form-inline'>
                  <select
                    id='majorAccountingClass'
                    name='majorAccountingClass'
                    className='form-control col-sm'
                    onChange={formik.handleChange}
                    value={formik.values.majorAccountingClass}>
                    {selectDropdowns.majorAccountingClasses.map((value, index) => {
                      return (
                        <option value={value.code}>
                          {value.code} - {value.description}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Major Equipment Class {<i className='fa fa-clone' />}</label>
                <div className='form-inline'>
                  <select
                    className='form-control col-sm'
                    id='majorEquipmentClass'
                    name='majorEquipmentClass'
                    onChange={formik.handleChange}
                    value={formik.values.majorEquipmentClass}>
                    {selectDropdowns.majorEquipmentClasses.map((value, index) => {
                      return (
                        <option value={value.Code}>
                          {value.Code} - {value.Description}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Manufacturer {<i className='fa fa-clone' />}</label>
                <select
                  id='manufacturer'
                  name='manufacturer'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.manufacturer}>
                  {selectDropdowns.manufacturers.map((value, index) => {
                    return (
                      <option value={value.Code}>
                        {value.Code} - {value.Description}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>
                  Department&nbsp;<span style={{ color: 'red' }}>*</span>&nbsp;{<i className='fa fa-clone' />}
                </label>
                <select
                  required={!showDisabledFields}
                  id='department'
                  name='department'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.department}>
                  {selectDropdowns.departments.map((value, index) => {
                    return (
                      <option value={value.Code}>
                        {value.Code} - {value.Description}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          </div>
          <div className='row justify-content-start'>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Function {<i className='fa fa-clone' />}</label>
                <select id='function' name='function' className='form-control col-sm' onChange={formik.handleChange} value={formik.values.function}>
                  {selectDropdowns.functions.map((value, index) => {
                    return (
                      <option value={value.Code}>
                        {value.Code} - {value.Description}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className='col-sm-3'>
              <div className='form-group'>
                <label>Category Code 6 {<i className='fa fa-clone' />}</label>
                <select
                  id='categoryCode6'
                  name='categoryCode6'
                  className='form-control col-sm'
                  onChange={formik.handleChange}
                  value={formik.values.categoryCode6}>
                  {selectDropdowns.catCode6.map((value, index) => {
                    return (
                      <option value={value.Code}>
                        {value.Code} - {value.Description}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className='col-sm-4 mt-1'>
          <div className='form-group'>
            <label>Add Comment</label>
            <textarea className='form-control col-sm' id='comment' rows={1} value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>
          <Comments barcode={props.queryData.requestedItemById.barcode} style={{ height: '450px' }} />
        </div>
      </div>

      <div className='requestDivider'>Additional Info</div>
      <div className='row justify-content-start mt-1'>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>Remarks 1 {<i className='fa fa-clone' />}</label>
            <div className='form-inline'>
              <input
                id='remarks1'
                name='remarks1'
                className='form-control col-sm'
                onChange={formik.handleChange}
                value={formik.values.remarks1 || ''}
                maxLength={30}
              />
            </div>
          </div>
        </div>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>Remarks 2</label>
            <input
              maxLength={30}
              id='remarks2'
              name='remarks2'
              className='form-control col-sm'
              onChange={formik.handleChange}
              value={formik.values.remarks2}
            />
          </div>
        </div>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>Remarks 3</label>
            <input
              maxLength={25}
              id='remarks3'
              name='remarks3'
              className='form-control col-sm'
              onChange={formik.handleChange}
              value={formik.values.remarks3}
            />
          </div>
        </div>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>
              Physical Owner&nbsp;<span style={{ color: 'red' }}>*</span>
            </label>
            <select
              id='physicalOwner'
              name='physicalOwner'
              className='form-control col-sm'
              required={!showDisabledFields}
              onChange={formik.handleChange}
              value={formik.values.physicalOwner}>
              {selectDropdowns.physicalOwners.map((value, index) => {
                return (
                  <option value={value.Code}>
                    {value.Code} - {value.Description}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>
      <div className='row justify-content-start'>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>Original Purchaser</label>
            <select
              id='originalPurchaser'
              name='originalPurchaser'
              className='form-control col-sm'
              onChange={formik.handleChange}
              value={formik.values.originalPurchaser}>
              {selectDropdowns.originalPurchasers.map((value, index) => {
                return (
                  <option value={value.Code}>
                    {value.Code} - {value.Description}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>New or Used {<i className='fa fa-clone' />} </label>
            <select id='newOrUsed' name='newOrUsed' className='form-control' onChange={formik.handleChange} value={formik.values.newOrUsed}>
              <option value='N'>New</option>
              <option value='U'>Used</option>
            </select>
          </div>
        </div>
        {/* <div className='col-sm-2'>
          <div className='form-group'>
            <label>New or Used {<i className='fa fa-clone' />}</label>
            <div className='form-inline'>
              <input id='newOrUsed' name='newOrUsed' className='form-control col-sm' onChange={formik.handleChange} value={formik.values.newOrUsed} />
            </div>
          </div>
        </div> */}
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>Delivery Date</label>
            <input
              id='deliveryDate'
              name='deliveryDate'
              className='form-control col-sm'
              onChange={formik.handleChange}
              value={formik.values.deliveryDate}
            />
          </div>
        </div>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>EOne Entry Date</label>
            <input
              id='dateEnteredInEnterpriseOne'
              name='dateEnteredInEnterpriseOne'
              className='form-control col-sm'
              onChange={formik.handleChange}
              value={formik.values.dateEnteredInEnterpriseOne}
            />
          </div>
        </div>
      </div>
      {/*extra, non-eone fields*/}
      <div className='row justify-content-start'>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>Amount Paid&nbsp;<span style={{color: 'red'}}>*</span></label>
            <input
              id='amountPaid'
              name='amountPaid'
              required
              className='form-control col-sm'
              onChange={formik.handleChange}
              value={formik.values.amountPaid}
            />
          </div>
        </div>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>Attachment</label>
            <input
              id='attachment'
              name='attachment'
              className='form-control col-sm'
              onChange={formik.handleChange}
              value={formik.values.attachment}
            />
          </div>
        </div>
        <div className='col-sm-2'>
          <div className='form-group'>
            <label>Covid Related</label>
            <select id='isCovid' name='isCovid' className='form-control' onChange={formik.handleChange} value={formik.values.isCovid}>
              <option value='true'>Yes</option>
              <option value='false'>No</option>
            </select>
          </div>
        </div>
      </div>
      <label className='mt-3 ml-3 float-left'>
        {<i className='fa fa-clone' />} These fields are applied to all items in a request when 'Bulk Update' is clicked
      </label>
    </Form>
  );
};

export default EditRequestedItemForm;
